import { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Search,
  Globe,
  Check,
  Star,
  MapPin,
  ArrowLeft,
  X
} from "lucide-react";
import {
  LANGUAGE_REGIONS,
  searchLanguages,
  getPopularLanguages,
  detectUserLanguage,
  type Language,
  type LanguageRegion
} from "@/lib/languages";
import { cn } from "@/lib/utils";

interface LanguageSelectorProps {
  onLanguageSelect: (languageCode: string) => void;
  selectedLanguage?: string;
  className?: string;
  compact?: boolean;
}

type ViewMode = 'popular' | 'regions' | 'search' | 'all';

export function LanguageSelector({
  onLanguageSelect,
  selectedLanguage,
  className,
  compact = false
}: LanguageSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>('popular');
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // NOTE: Auto-detection removed. Parent components (e.g., Step2Language) 
  // should handle initial language selection to avoid overwriting user choices.

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchLanguages(searchQuery);
  }, [searchQuery]);

  const popularLanguages = useMemo(() => getPopularLanguages(), []);

  const handleLanguageSelect = (language: Language) => {
    onLanguageSelect(language.code);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      setViewMode('search');
    } else {
      // Revert to the last non-search view or 'popular'
      const previousMode = (['search', 'popular', 'regions', 'all'] as ViewMode[])
        .find(mode => mode === viewMode && mode !== 'search') || 'popular';
      setViewMode(previousMode);
    }
  };

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    setSearchQuery("");
    setExpandedRegion(null);
  };

  const handleRegionClick = (regionName: string) => {
    if (expandedRegion === regionName) {
      setExpandedRegion(null);
    } else {
      setExpandedRegion(regionName);
    }
  };

  const LanguageItem = ({ language, isSelected = false }: {
    language: Language;
    isSelected?: boolean;
  }) => (
    <button
      onClick={() => handleLanguageSelect(language)}
      className={cn(
        "w-full flex items-center justify-between p-3 rounded-lg transition-all duration-200",
        "hover:bg-muted/50 focus:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/20",
        isSelected && "bg-primary/10 border border-primary/20",
        language.rtl && "text-right"
      )}
      dir={language.rtl ? "rtl" : "ltr"}
    >
      <div className="flex items-center space-x-3">
        {/* Adjusted flag size for mobile visibility */}
        <span className="text-xl sm:text-2xl" role="img" aria-label={`${language.name} flag`}>
          {language.flag}
        </span>
        <div className="text-left">
          <div className={cn(
            "font-medium text-foreground text-base sm:text-lg", // Adjusted text size
            compact && "text-sm"
          )}>
            {language.name}
          </div>
          <div className={cn(
            "text-sm text-muted-foreground",
            compact && "text-xs"
          )}>
            {language.nativeName}
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        {language.popular && (
          <Badge variant="secondary" className="text-xs px-2 py-0.5 hidden sm:flex">
            <Star className="w-3 h-3 mr-1" />
            Popular
          </Badge>
        )}
        {isSelected && (
          <Check className="w-4 h-4 text-primary" />
        )}
      </div>
    </button>
  );

  const RegionSection = ({ region }: { region: LanguageRegion }) => {
    const isExpanded = expandedRegion === region.name;
    // Show max 3 languages initially, which is good for mobile
    const displayLanguages = isExpanded ? region.languages : region.languages.slice(0, 3);

    return (
      <div className="space-y-2">
        <button
          onClick={() => handleRegionClick(region.name)}
          className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted/30 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium text-sm">{region.name}</span>
            <Badge variant="outline" className="text-xs">
              {region.languages.length}
            </Badge>
          </div>
          <span className="text-muted-foreground text-sm">
            {isExpanded ? '−' : '+'}
          </span>
        </button>

        <div className="space-y-1 ml-4 sm:ml-6"> {/* Reduced margin-left on mobile */}
          {displayLanguages.map((language) => (
            <LanguageItem
              key={language.code}
              language={language}
              isSelected={selectedLanguage === language.code}
            />
          ))}

          {!isExpanded && region.languages.length > 3 && (
            <button
              onClick={() => handleRegionClick(region.name)}
              className="w-full text-left p-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              +{region.languages.length - 3} more languages...
            </button>
          )}
        </div>
      </div>
    );
  };

  // Compact Mode (Already mostly responsive, just ensuring proper sizing)
  if (compact) {
    return (
      <div className={cn("w-full max-w-sm", className)}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            ref={searchInputRef}
            placeholder="Search languages..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10 text-base" // Ensure base font size for input
          />
        </div>

        {/* Fixed smaller height for compact mode */}
        <ScrollArea className="h-[18rem] mt-2">
          <div className="space-y-1">
            {(searchQuery ? searchResults : popularLanguages).map((language) => (
              <LanguageItem
                key={language.code}
                language={language}
                isSelected={selectedLanguage === language.code}
              />
            ))}
          </div>
        </ScrollArea>
      </div>
    );
  }

  // Full Mode
  return (
<div
  className={cn(
    "w-full max-w-2xl mx-auto px-3 sm:px-0 overflow-x-hidden",
    className
  )}
>
  {/* Header */}
  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
    <div className="flex items-center gap-2">
      <Globe className="w-4 h-4 sm:w-6 sm:h-6 text-primary shrink-0" />
      <h2 className="text-lg sm:text-2xl font-bold leading-tight">
        Choose Your Language
      </h2>
    </div>
    <div className="text-[11px] sm:text-sm text-muted-foreground leading-tight">
      {LANGUAGE_REGIONS.reduce((acc, r) => acc + r.languages.length, 0)} languages
    </div>
  </div>

  {/* Search Bar */}
  <div className="relative mb-4 p-1">
    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
    <Input
      ref={searchInputRef}
      placeholder="Search language..."
      value={searchQuery}
      onChange={(e) => handleSearch(e.target.value)}
      className="h-9 pl-9 pr-9 text-sm sm:h-11 sm:text-base"
    />
    {searchQuery && (
      <Button
        variant="ghost"
        size="icon"
        onClick={() => handleSearch("")}
        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
      >
        <X className="w-3.5 h-3.5" />
      </Button>
    )}
  </div>

  {/* View Mode Tabs */}
  <div className="flex flex-wrap gap-1 mb-4 bg-muted p-1 rounded-lg">
  {[
    { key: "popular", label: "Popular", icon: Star },
    { key: "regions", label: "Regions", icon: MapPin },
    { key: "all", label: "All", icon: Globe },
  ].map(({ key, label, icon: Icon }) => (
    <Button
      key={key}
      variant={viewMode === key ? "secondary" : "ghost"}
      size="sm"
      onClick={() => handleViewChange(key as any)}
      className="
        flex-1 
        min-w-0       /* Allow shrinking below 90px */
        sm:min-w-[90px] 
        h-8 
        text-xs sm:text-sm
        flex justify-center items-center
      "
    >
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Button>
  ))}
</div>

  {/* Content */}
  <ScrollArea className="h-[55vh] sm:h-96">
    <div className="space-y-4 pr-1">
      {/* SEARCH */}
      {viewMode === "search" && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-sm sm:text-lg font-semibold">Search Results</h3>
            <Badge variant="outline" className="text-[10px]">
              {searchResults.length}
            </Badge>
          </div>

          {searchResults.length ? (
            <div className="space-y-2">
              {searchResults.map((language) => (
                <LanguageItem
                  key={language.code}
                  language={language}
                  isSelected={selectedLanguage === language.code}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground text-sm">
              No results found
            </div>
          )}
        </>
      )}

      {/* POPULAR */}
      {viewMode === "popular" && (
        <>
          <h3 className="text-sm sm:text-lg font-semibold flex items-center gap-2">
            <Star className="w-4 h-4 text-yellow-500" />
            Popular Languages
          </h3>
          <div className="space-y-2">
            {popularLanguages.map((language) => (
              <LanguageItem
                key={language.code}
                language={language}
                isSelected={selectedLanguage === language.code}
              />
            ))}
          </div>
        </>
      )}

      {/* REGIONS */}
      {viewMode === "regions" &&
        LANGUAGE_REGIONS.filter(r => r.name !== "Popular").map((region) => (
          <RegionSection key={region.name} region={region} />
        ))}

      {/* ALL */}
      {viewMode === "all" && (
        <>
          <h3 className="text-sm sm:text-lg font-semibold flex items-center gap-2">
            <Globe className="w-4 h-4 text-primary" />
            All Languages
          </h3>
          <div className="space-y-2">
            {LANGUAGE_REGIONS.flatMap(r => r.languages)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((language) => (
                <LanguageItem
                  key={language.code}
                  language={language}
                  isSelected={selectedLanguage === language.code}
                />
              ))}
          </div>
        </>
      )}
    </div>
  </ScrollArea>

  {/* Footer */}
  <div className="mt-4 p-3 bg-muted/30 rounded-lg text-[11px] sm:text-sm">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        <Globe className="w-3 h-3 shrink-0" />
        <span className="leading-tight">
          Messages will be translated automatically
        </span>
      </div>

      {selectedLanguage && (
        <Badge variant="secondary" className="text-[10px]">
          Selected:{" "}
          {
            LANGUAGE_REGIONS.flatMap(r => r.languages)
              .find(l => l.code === selectedLanguage)?.name
          }
        </Badge>
      )}
    </div>
  </div>
</div>

  );
}